import { useReducer } from 'react';
import { useNavigate, Navigate, Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { stringifyPropertyValue } from '@sloy/shared';
import { useAuthStore } from '@/stores/authStore';
import { api, ApiRequestError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { AuthLogo } from '@/features/auth/AuthLogo';
import { AuthTabs } from '@/features/auth/AuthTabs';
import { useI18n } from '@/i18n/useI18n';
import { resendErrorKey } from './verificationErrors';

interface LoginUiState {
  email: string;
  password: string;
  error: string;
  loading: boolean;
  resendHint: boolean;
  resendLoading: boolean;
  resendInfo: string;
}

const initialLoginUi: LoginUiState = {
  email: '',
  password: '',
  error: '',
  loading: false,
  resendHint: false,
  resendLoading: false,
  resendInfo: '',
};

type LoginUiAction =
  | { type: 'set-email'; value: string }
  | { type: 'set-password'; value: string }
  | { type: 'before-submit' }
  | { type: 'submit-done' }
  | { type: 'set-error'; message: string }
  | { type: 'login-email-not-verified' }
  | { type: 'before-resend' }
  | { type: 'resend-done' }
  | { type: 'set-resend-info'; message: string };

function loginUiReducer(state: LoginUiState, action: LoginUiAction): LoginUiState {
  switch (action.type) {
    case 'set-email':
      return { ...state, email: action.value };
    case 'set-password':
      return { ...state, password: action.value };
    case 'before-submit':
      return {
        ...state,
        error: '',
        resendHint: false,
        resendInfo: '',
        loading: true,
      };
    case 'submit-done':
      return { ...state, loading: false };
    case 'set-error':
      return { ...state, error: action.message };
    case 'login-email-not-verified':
      return { ...state, resendHint: true };
    case 'before-resend':
      return { ...state, resendLoading: true, resendInfo: '', error: '' };
    case 'resend-done':
      return { ...state, resendLoading: false };
    case 'set-resend-info':
      return { ...state, resendInfo: action.message };
    default:
      return state;
  }
}

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [ui, dispatch] = useReducer(loginUiReducer, initialLoginUi);
  const { t, language } = useI18n();

  // Если пользователь уже авторизован — перенаправляем.
  // Через <Navigate>, а не navigate() в теле рендера: рендер должен оставаться чистым,
  // иначе под StrictMode/конкурентным рендерингом переход в историю уходит дважды.
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (ui.loading) {
      return;
    }

    const formData = new FormData(e.currentTarget);
    const email = stringifyPropertyValue(formData.get('email')).trim();
    const password = stringifyPropertyValue(formData.get('password'));

    dispatch({ type: 'before-submit' });
    try {
      const data = await api.post('/auth/login', { email, password });
      queryClient.clear();
      setAuth(data.user, data.tokens);
      void navigate('/dashboard');
    } catch (err) {
      const isEmailNotVerified = err instanceof ApiRequestError && err.code === 'EMAIL_NOT_VERIFIED';
      if (isEmailNotVerified) {
        dispatch({ type: 'login-email-not-verified' });
      }
      dispatch({
        type: 'set-error',
        message: isEmailNotVerified
          ? t('login.emailNotVerified')
          : err instanceof Error
            ? err.message
            : t('login.error'),
      });
    } finally {
      dispatch({ type: 'submit-done' });
    }
  }

  async function handleResendVerification() {
    const emailNorm = ui.email.trim().toLowerCase();
    if (!emailNorm) {
      dispatch({ type: 'set-resend-info', message: '' });
      dispatch({ type: 'set-error', message: t('login.emailRequired') });
      return;
    }
    dispatch({ type: 'before-resend' });
    try {
      await api.post('/auth/resend-verification', { email: emailNorm, language });
      dispatch({
        type: 'set-resend-info',
        message: t('login.checkInbox'),
      });
    } catch (e) {
      dispatch({
        type: 'set-error',
        message: t(resendErrorKey(e)),
      });
    } finally {
      dispatch({ type: 'resend-done' });
    }
  }

  return (
    <div className="relative flex min-h-[max(100vh,700px)] items-center justify-center bg-muted p-4 pb-20">
      <AuthLogo />
      <Card className="w-full max-w-md gap-8 rounded-3xl pt-8 pb-10">
        <CardHeader className="px-12">
          <AuthTabs active="login" />
        </CardHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-7">
          <CardContent className="flex flex-col gap-4 px-12">
            {ui.error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {ui.error}
              </div>
            )}
            {ui.resendInfo && (
              <div className="rounded-md bg-muted/60 p-3 text-sm text-foreground">{ui.resendInfo}</div>
            )}
            {ui.resendHint && (
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  disabled={ui.resendLoading}
                  onClick={handleResendVerification}
                >
                  {ui.resendLoading ? t('login.resending') : t('login.resend')}
                </Button>
                <Link
                  to="/verify-email"
                  state={{ email: ui.email.trim().toLowerCase() }}
                  className="text-center text-sm text-primary border-b border-current/15 hover:border-current/40 self-center"
                >
                  {t('login.enterCode')}
                </Link>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-medium">
                {t('auth.email')}
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                value={ui.email}
                onChange={(e) => dispatch({ type: 'set-email', value: e.target.value })}
                onInput={(e) => dispatch({ type: 'set-email', value: e.currentTarget.value })}
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium">
                  {t('login.password')}
                </label>
                <Link
                  to="/forgot-password"
                  className="inline-block text-sm text-muted-foreground hover:text-primary border-b border-current/15 hover:border-current/40"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <PasswordInput
                id="password"
                name="password"
                placeholder={t('login.passwordPlaceholder')}
                showPasswordLabel={t('password.show')}
                hidePasswordLabel={t('password.hide')}
                value={ui.password}
                onChange={(e) => dispatch({ type: 'set-password', value: e.target.value })}
                onInput={(e) => dispatch({ type: 'set-password', value: e.currentTarget.value })}
                required
                autoComplete="current-password"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 px-12">
            <Button type="submit" className="w-full" disabled={ui.loading}>
              {ui.loading ? t('login.submitting') : t('login.submit')}
            </Button>
          </CardFooter>
        </form>
      </Card>
      <LanguageSwitcher className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#f5f5f5] dark:bg-[#262626]" />
    </div>
  );
}
